import React, { useEffect, useState } from 'react';

import { Box } from '@rbilabs/universal-components';
import { PermissionStatus, useCameraPermissions } from 'expo-camera';
import { useIntl } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import ConfirmDialog from 'components/confirm-dialog';
import Dialog from 'components/dialog';
import { primitive } from 'styles/constants/primitives';
import { openPushSetting as openDeviceSettings } from 'utils/permissions';

import { CameraScreen } from './camera-screen';
import { PhotoReviewScreen } from './photo-review-screen';
import { CameraProps, Mode, Photo } from './types';
import { processPhoto } from './utils';

export const Camera = ({
  onClose,
  callback,
  flip,
  options = {},
  onCameraAccessDenied,
}: CameraProps) => {
  const {
    promptText,
    flippable,
    format,
    maxSize,
    maxWidth,
    maxHeight,
    enablePreview,
    imageOverlay,
    textInstructions,
    fixMirroring = false,
  } = options;

  const { formatMessage } = useIntl();
  const [mode, setMode] = useState(Mode.Camera);
  const [photo, setPhoto] = useState<Photo | null>(null);
  const [permission, requestPermission] = useCameraPermissions();
  const [showAllowCameraAccessModal, setShowAllowCameraAccessModal] = useState(false);

  useEffect(() => {
    // permission can be UNDERTERMINED, DENIED or GRANTED,
    // but some devices may not prompt for permission that's why
    // we need to request if status !== Granted
    if (permission?.status === PermissionStatus.DENIED) {
      setShowAllowCameraAccessModal(true);
      onCameraAccessDenied && onCameraAccessDenied();
    } else if (permission?.status !== PermissionStatus.GRANTED) {
      requestPermission();
    }
  }, [onCameraAccessDenied, permission, requestPermission, showAllowCameraAccessModal]);

  const updateAllowCameraAccess = () => {
    setShowAllowCameraAccessModal(false);
    onClose();
  };

  const updateMode = (mode: Mode) => {
    setMode(mode);
  };

  const onTakePhoto = async (photo: Photo) => {
    if (!enablePreview) {
      const photoBase64 = await processPhoto(
        photo,
        flip,
        format,
        maxSize,
        maxWidth,
        maxHeight,
        fixMirroring
      );
      submitPhoto(photoBase64);
    } else {
      setPhoto(photo);
      updateMode(Mode.Review);
    }
  };

  const submitPhoto = async (photo: string) => {
    callback(photo);
  };

  const backgroundColorScreen: string =
    Platform.OS === 'web' ? primitive.$blackOpacity70 : primitive.$black;
  // Camera will be ready to show only if we grant permissions first
  // if not then we need to request permission
  const isCameraReady = permission?.status === PermissionStatus.GRANTED;

  return (
    <SafeAreaView style={[styles.absoluteFill, { backgroundColor: backgroundColorScreen }]}>
      <Box flexDirection={'column'} justifyContent="center" alignItems="center">
        {mode === Mode.Camera && photo === null ? (
          <CameraScreen
            flip={flip}
            onTakePhoto={onTakePhoto}
            onClose={onClose}
            promptText={promptText}
            flippable={!!flippable}
            imageOverlay={imageOverlay}
            textInstructions={textInstructions}
            isCameraReady={isCameraReady}
          />
        ) : (
          enablePreview &&
          photo !== null && (
            <PhotoReviewScreen
              photo={photo}
              onClose={onClose}
              onRetake={() => {
                updateMode(Mode.Camera);
                setPhoto(null);
              }}
              onSubmit={submitPhoto}
              flip={flip}
              format={format}
              maxSize={maxSize}
              maxHeight={maxHeight}
              maxWidth={maxWidth}
              fixMirroring={fixMirroring}
            />
          )
        )}
      </Box>
      {Platform.OS !== 'web' && (
        <ConfirmDialog
          showDialog={showAllowCameraAccessModal}
          heading={formatMessage({ id: 'allowCameraAccess' })}
          body={formatMessage({ id: 'goToDeviceSettings' })}
          cancelLabel={formatMessage({ id: 'openDeviceSettings' })}
          confirmLabel={formatMessage({ id: 'notNow' })}
          onDismiss={updateAllowCameraAccess}
          onCancel={updateAllowCameraAccess}
          onConfirm={() => {
            openDeviceSettings();
            updateAllowCameraAccess();
          }}
          modalAppearanceEventMessage="Confirm Open device Camera Access settings"
        />
      )}
      {Platform.OS === 'web' && (
        <Dialog
          showDialog={showAllowCameraAccessModal}
          modalAppearanceEventMessage={formatMessage({ id: 'allowCameraAccessWebTitle' })}
          heading={formatMessage({ id: 'allowCameraAccessWebTitle' })}
          body={formatMessage({ id: 'allowCameraAccessWebText' })}
          accessibilityLabel={formatMessage({ id: 'allowCameraAccessWebText' })}
          closeButtonLabelId={formatMessage({ id: 'notNow' })}
          showCloseButton
          onDismiss={() => {
            updateAllowCameraAccess();
          }}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  absoluteFill: {
    ...StyleSheet.absoluteFillObject,
  },
});
